import { useState } from "react";
import Reveal from "./Reveal";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES, SITE } from "@/lib/site-data";
import ServiceDetail from "./ServiceDetail";

export default function Services() {
  const [openId, setOpenId] = useState(null);
  const active = SERVICES.find((s) => s.id === openId) || null;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[color:var(--sr-cream)] py-20 lg:py-32"
      aria-label="Our services"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 lg:mb-20">
          <div>
            <Reveal>
              <p className="eyebrow eyebrow-dark">Services Designed For You</p>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] on-cream tracking-[-0.01em] mt-3">
                Explore Our Services
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 on-cream-muted text-[15px] leading-[1.75] max-w-[520px]">
                Three signature services designed to refine your natural beauty
                — click any card for details, benefits, and a Q&amp;A.
              </p>
            </Reveal>
          </div>
          <Reveal delay={180}>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-book-btn"
              className="btn-gold"
            >
              Book Now <ArrowRight size={14} strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.id}
              delay={i * 120}
              className="group cursor-pointer"
              data-testid={`service-card-${s.id}`}
            >
              <button
                onClick={() => setOpenId(s.id)}
                className="text-left w-full block bg-[color:var(--sr-card)] border border-[var(--sr-border)] transition-all duration-700 hover:border-[color:var(--sr-gold)]"
                aria-label={`Learn more about ${s.title}`}
                data-testid={`service-open-${s.id}`}
              >
                <div className="zoom-img aspect-[4/5] bg-[#0a0a0a]">
                  <img
                    src={s.image}
                    alt={s.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="px-6 pt-8 pb-8">
                  <h3 className="font-serif text-2xl lg:text-[28px] tracking-[-0.005em] text-[color:var(--sr-ivory)]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[color:var(--sr-beige)] text-[14px] leading-[1.7]">
                    {s.tagline}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 eyebrow text-[color:var(--sr-gold)] transition-transform duration-500 group-hover:translate-x-1">
                    View Service
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={400} className="mt-16 text-center">
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-more-services-link"
            className="inline-flex items-center gap-2 group on-cream tracking-[0.24em] uppercase text-[11.5px] font-medium border-b border-[#3a3126]/40 pb-2 hover:border-[color:var(--sr-taupe)] transition-colors"
          >
            View More of Our Services
            <ArrowUpRight
              size={14}
              strokeWidth={1.5}
              className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </Reveal>
      </div>

      <ServiceDetail
        service={active}
        open={!!active}
        onClose={() => setOpenId(null)}
      />
    </section>
  );
}
